import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { useAssessmentMetrics } from '@app/src/hooks/useAssessmentMetrics'
import Assessment from '@app/src/types/assessment'
import { SortOrder } from '@app/src/types/filter'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import paths from '@app/src/wf-constants/paths'
import { Description as DocumentIcon } from '@mui/icons-material'
import { Box, Button, Card, CircularProgress, Divider, Grid, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

const AssessmentCard: React.FC<{
  assessment: Assessment
}> = ({ assessment }) => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { getCircleColor, getCircleValue, getStatusText, getScoreText, getCircleText } =
    useAssessmentMetrics(assessment)

  const handleView = () => {
    const path =
      assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment
        ? paths.transparencyBaselineAssessment
        : paths.transparencyStandardAssessment

    history.push({
      pathname: generatePath(path, { id: assessment.id }),
    })
  }

  return (
    <Card
      sx={{
        height: '100%',
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2, flex: 1 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                bgcolor: 'grey.100',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
              }}
            >
              <DocumentIcon />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                {assessment.assessmentTemplate.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assessment.periodName}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              {getStatusText()}
            </Typography>
            <Box
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ width: 64, height: 64 }}
            >
              <Tooltip title={getScoreText()} arrow>
                <Box sx={{ position: 'relative', width: 64, height: 64 }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={64}
                    thickness={6}
                    sx={{
                      color: 'grey.100',
                      opacity: 1,
                    }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={getCircleValue()}
                    size={64}
                    thickness={6}
                    sx={{
                      color: getCircleColor(),
                      position: 'absolute',
                      left: 0,
                      top: 0,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {getCircleText()}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider
        sx={{
          margin: '0 16px',
        }}
      />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: '64px',
          padding: '12px 12px 12px 16px',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" fontWeight={400}>
            {formatMessage({ id: 'transparencyOverview.calculatedOn' })}{' '}
            {new Date(assessment.updatedAt || assessment.createdAt).toLocaleDateString('en-UK', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </Typography>
        </Box>
        <Button variant="contained" onClick={handleView}>
          {formatMessage({ id: 'general.view' })}
        </Button>
      </Box>
    </Card>
  )
}

const AssessmentCards: React.FC = () => {
  const { formatMessage } = useIntl()
  const userFilters = useGetApiQueryFilters(['assessmentTemplate.id', 'periodName', 'assessmentTemplateId'])

  const {
    items: assessments,
    isLoading,
    isError,
  } = useFetchCollectionWithPost<Assessment>({
    key: [FetchKey.AssessmentCollection],
    endpoint: endpoints.assessmentFilterOwnOrganization,
    payload: {
      filter: [...userFilters],
      include: ['assessmentTemplate'],
      sort: {
        target: 'updatedAt',
        order: SortOrder.DESCENDING,
      },
    },
  })

  if (isLoading || isError || !assessments || assessments.length === 0) {
    return null
  }

  // Group assessments by type and get the latest baseline assessment
  const baselineAssessment = assessments
    .filter(a => a.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment)
    .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())[0]

  const otherAssessments = assessments
    .filter(a => a.assessmentTemplate.assessmentType !== AssessmentType.BaselineAssessment)
    .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())

  // Combine baseline and other assessments
  const sortedAssessments = baselineAssessment ? [baselineAssessment, ...otherAssessments] : otherAssessments

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {formatMessage({ id: 'transparencyOverview.yourAssessments' })}
      </Typography>
      <Grid container spacing={2}>
        {sortedAssessments.map(assessment => (
          <Grid item xs={4} key={assessment.id}>
            <AssessmentCard assessment={assessment} />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default AssessmentCards
